import { ApiService, type ApiRequestConfig } from "./api.service";
import type { TProjectMembership, TProjectViewer } from "@/types/project";

export interface TAddProjectMembersData {
  members: { member_id: string; role: string }[];
}

export class ProjectMemberService extends ApiService {
  /**
   * The project's members. `q` narrows them by name or email on the server; a
   * blank one is no filter, the same request as none.
   */
  async listMembers(
    slug: string,
    projectId: string,
    { q }: { q?: string } = {},
    config?: ApiRequestConfig,
  ): Promise<TProjectMembership[]> {
    const query = q?.trim();
    return this.get(`/api/workspaces/${slug}/projects/${projectId}/members/`, {
      ...config,
      ...(query ? { params: { q: query } } : {}),
    });
  }

  async addMembers(
    slug: string,
    projectId: string,
    data: TAddProjectMembersData,
  ): Promise<TProjectMembership[]> {
    return this.post(`/api/workspaces/${slug}/projects/${projectId}/members/`, data);
  }

  async updateMember(
    slug: string,
    projectId: string,
    memberId: string,
    data: { role: string },
  ): Promise<TProjectMembership> {
    return this.patch(`/api/workspaces/${slug}/projects/${projectId}/members/${memberId}/`, data);
  }

  async removeMember(slug: string, projectId: string, memberId: string): Promise<void> {
    return this.delete(`/api/workspaces/${slug}/projects/${projectId}/members/${memberId}/`);
  }

  async memberMe(
    slug: string,
    projectId: string,
    config?: ApiRequestConfig,
  ): Promise<TProjectMembership> {
    return this.get(`/api/workspaces/${slug}/projects/${projectId}/project-members/me/`, config);
  }

  async listViewers(
    slug: string,
    projectId: string,
    config?: ApiRequestConfig,
  ): Promise<TProjectViewer[]> {
    // Server-paginated (issue #1254); walk the pages so the viewers panel keeps
    // the full set it renders locally. UI is unchanged.
    return this.getAllPages<TProjectViewer>(
      ({ cursor, perPage }) => {
        const qs = new URLSearchParams({ per_page: String(perPage) });
        if (cursor) qs.set("cursor", cursor);
        return `/api/workspaces/${slug}/projects/${projectId}/viewers/?${qs}`;
      },
      undefined,
      config,
    );
  }

  /** Omit memberId to add the current user (self-add). */
  async addViewer(slug: string, projectId: string, memberId?: string): Promise<TProjectViewer> {
    return this.post(
      `/api/workspaces/${slug}/projects/${projectId}/viewers/`,
      memberId ? { member_id: memberId } : {},
    );
  }

  async removeViewer(slug: string, projectId: string, bindingId: string): Promise<void> {
    return this.delete(`/api/workspaces/${slug}/projects/${projectId}/viewers/${bindingId}/`);
  }
}
